import { useRecoilState } from 'recoil';
import { asrPlexWebSocketState } from './state';
import { asrPlexWebSocketService } from './ASRPlexWebSocketService';
import { useEffect, useRef } from 'react';
import { logger } from 'modules/logger';
import { getBackoffDelayMs } from './utils';
import { useConnectionUrls } from 'hooks/useConfigureClusterConnections';

export const useRetryASRPlexWebSocket = () => {
  const [socketState, setSocketState] = useRecoilState(asrPlexWebSocketState);
  const attemptsRef = useRef(0);
  const { asrPlexWs } = useConnectionUrls();

  const MAX_ATTEMPTS_TO_WARN = 5;

  useEffect(() => {
    let socket = asrPlexWebSocketService.connect(asrPlexWs);
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let disposed = false;

    const onOpen = () => {
      if (disposed) return;

      // Reset the attempts counter
      attemptsRef.current = 1;
      setSocketState((prev) => ({
        ...prev,
        socket,
      }));
    };
    const onClose = (event: CloseEvent) => {
      // Cleanup previous event listeners
      socket.removeEventListener('open', onOpen);
      socket.removeEventListener('close', onClose);

      setSocketState((prev) =>
        prev.socket === socket
          ? {
              ...prev,
              socket: null,
            }
          : prev
      );

      if (disposed) return;

      if (attemptsRef.current >= MAX_ATTEMPTS_TO_WARN) {
        logger.warn(
          `[ASRPlexDictation] WebSocket has retried ${attemptsRef.current} times (≥ ${MAX_ATTEMPTS_TO_WARN}).`
        );
      }

      const backoff = getBackoffDelayMs(attemptsRef.current);
      const jitter = Math.floor(Math.random() * 50);

      const { code, reason, wasClean } = event;

      logger.info(
        `[ASRPlexDictation] WebSocket connection closed, retrying connection in ${
          backoff + jitter
        } ms...`,
        {
          attempts: attemptsRef.current,
          backoff,
          jitter,
          closeEvent: { code, reason, wasClean },
        }
      );
      retryTimer = setTimeout(() => {
        if (disposed) return;

        socket = asrPlexWebSocketService.connect(asrPlexWs);
        attemptsRef.current += 1;
        socket.addEventListener('open', onOpen);
        socket.addEventListener('close', onClose);
      }, backoff + jitter);
    };
    socket.addEventListener('open', onOpen);
    socket.addEventListener('close', onClose);
    if (socket.readyState === WebSocket.OPEN) {
      onOpen();
    }

    return () => {
      disposed = true;
      if (retryTimer != null) {
        clearTimeout(retryTimer);
      }
      socket.removeEventListener('open', onOpen);
      socket.removeEventListener('close', onClose);
      setSocketState((prev) =>
        prev.socket === socket
          ? {
              ...prev,
              socket: null,
            }
          : prev
      );
    };
  }, [setSocketState, asrPlexWs]);

  return socketState.socket;
};
